import {
  Anchor,
  Badge,
  Button,
  FileButton,
  Group,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PiCheckCircle, PiUploadSimple } from 'react-icons/pi';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import { formatDate } from '@/lib/helpers';
import {
  apiErrorText,
  type ClientCompanyCard,
  type CompanyDocument,
  DOCUMENT_REQUIREMENT_LABELS,
  DOCUMENT_REQUIREMENTS,
  type DocumentRequirement,
  downloadFile,
  useAssignCompanyDocument,
  useUploadCompanyDocuments,
} from '@/models/clientCompanies';

const REQUIREMENT_OPTIONS = DOCUMENT_REQUIREMENTS.map((value) => ({
  value,
  label: DOCUMENT_REQUIREMENT_LABELS[value],
}));

export function CompanyDocumentsCard({
  card,
  canWrite,
}: {
  card: ClientCompanyCard;
  canWrite: boolean;
}) {
  const companyId = Number(card.company.id);
  const upload = useUploadCompanyDocuments(companyId);
  const assign = useAssignCompanyDocument();
  const required = new Map(
    card.requirements.documents.map((d) => [d.requirement, d.filled]),
  );
  const missing = card.requirements.documents.filter((d) => !d.filled).length;
  const unassigned = card.documents.filter((d) => d.requirements.length === 0);

  const fail = (title: string) => (e: unknown) =>
    notifications.show({ color: 'red', title, message: apiErrorText(e) });

  const onUpload = (requirement: DocumentRequirement) => (files: File[]) =>
    files.length > 0 &&
    upload.mutateAsync({ requirement, files }).catch(fail('Upload failed'));

  const onAssign = (doc: CompanyDocument, requirement: string | null) =>
    requirement &&
    assign
      .mutateAsync({
        companyId,
        documentId: Number(doc.id),
        requirement: requirement as DocumentRequirement,
      })
      .catch(fail('Document not assigned'));

  const docRow = (doc: CompanyDocument) => (
    <Group key={doc.id} gap="xs" justify="space-between" wrap="nowrap">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Anchor
          fz="xs"
          component="button"
          ta="left"
          onClick={() =>
            downloadFile(
              'client-companies/document-download',
              { companyId, documentId: Number(doc.id) },
              doc.filename ?? `document-${doc.id}`,
            ).catch(fail('Download failed'))
          }
        >
          {doc.filename ?? doc.name ?? `Document #${doc.id}`}
        </Anchor>
        <Text fz={10} c="dimmed">
          {formatDate(doc.createdAt)}
          {doc.fromMonday && doc.requirements.length === 0
            ? ' - in the Monday item'
            : ''}
        </Text>
      </Stack>
      {canWrite && (
        <Select
          size="xs"
          w={170}
          placeholder="Assign to..."
          aria-label="Requirement"
          data={REQUIREMENT_OPTIONS}
          value={doc.requirements[0] ?? null}
          onChange={(v) => onAssign(doc, v)}
          allowDeselect={false}
        />
      )}
    </Group>
  );

  return (
    <CollapsibleCard
      title="Documents"
      rightSection={
        missing > 0 ? (
          <Badge size="xs" color="orange" variant="light">
            {missing} required missing
          </Badge>
        ) : (
          <Badge size="xs" color="green" variant="light">
            Complete
          </Badge>
        )
      }
    >
      <Stack gap="sm">
        {DOCUMENT_REQUIREMENTS.map((req) => {
          const docs = card.documents.filter((d) =>
            d.requirements.includes(req),
          );
          const isRequired = required.has(req);
          const done = docs.length > 0;
          return (
            <Stack key={req} gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Group gap={6}>
                  <Text fz="sm" fw={500}>
                    {DOCUMENT_REQUIREMENT_LABELS[req]}
                    {isRequired && (
                      <Text span c="red">
                        {' '}
                        *
                      </Text>
                    )}
                  </Text>
                  {done ? (
                    <PiCheckCircle color="var(--mantine-color-green-6)" />
                  ) : (
                    isRequired && (
                      <Badge size="xs" color="orange" variant="light">
                        Missing
                      </Badge>
                    )
                  )}
                </Group>
                {canWrite && (
                  <FileButton onChange={onUpload(req)} multiple>
                    {(props) => (
                      <Button
                        {...props}
                        size="compact-xs"
                        variant="subtle"
                        leftSection={<PiUploadSimple size={12} />}
                        loading={
                          upload.isPending &&
                          upload.variables?.requirement === req
                        }
                      >
                        Upload
                      </Button>
                    )}
                  </FileButton>
                )}
              </Group>
              {docs.map(docRow)}
            </Stack>
          );
        })}
        {unassigned.length > 0 && (
          <Stack gap={4}>
            <Text fz="xs" fw={600} c="dimmed" tt="uppercase">
              Not assigned ({unassigned.length})
            </Text>
            <Text fz="xs" c="dimmed">
              Files already in BO for this company. Assign one to a requirement
              instead of uploading it again.
            </Text>
            {unassigned.map(docRow)}
          </Stack>
        )}
      </Stack>
    </CollapsibleCard>
  );
}
